import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Platform, ScrollView, TouchableOpacity, ActivityIndicator, TextInput, Alert, Modal, Pressable } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { usePersistentWidget } from '../context/PersistentWidgetContext';
import DashboardSidebar from '../components/DashboardSidebar';
import AnimatedCard from '../components/AnimatedCard';
import { persistentWidgetService } from '../services/persistentWidgetService';

// Dropdown Component
const Dropdown = ({ value, options, onSelect, placeholder = 'Select...', style }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (option) => {
    onSelect(option);
    setIsOpen(false);
  };

  return (
    <View style={[dropdownStyles.container, style]}>
      <TouchableOpacity 
        style={dropdownStyles.button}
        onPress={() => setIsOpen(!isOpen)}
        activeOpacity={0.7}
      >
        <Text style={dropdownStyles.buttonText} numberOfLines={1}>
          {value || placeholder}
        </Text>
        <MaterialCommunityIcons 
          name={isOpen ? "menu-up" : "menu-down"} 
          size={18} 
          color="#000" 
        />
      </TouchableOpacity>
      
      {isOpen && (
        <Modal
          transparent
          visible={isOpen}
          animationType="fade"
          onRequestClose={() => setIsOpen(false)}
        >
          <Pressable 
            style={dropdownStyles.modalOverlay}
            onPress={() => setIsOpen(false)}
          >
            <View style={dropdownStyles.menuContainer}>
              <ScrollView style={dropdownStyles.menu} nestedScrollEnabled>
                {options.map((option, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      dropdownStyles.option,
                      value === option && dropdownStyles.optionSelected,
                      index === 0 && dropdownStyles.optionFirst,
                      index === options.length - 1 && dropdownStyles.optionLast,
                    ]}
                    onPress={() => handleSelect(option)}
                    activeOpacity={0.7}
                  >
                    <Text style={[
                      dropdownStyles.optionText,
                      value === option && dropdownStyles.optionTextSelected,
                    ]}>
                      {option || placeholder}
                    </Text>
                    {value === option && (
                      <MaterialCommunityIcons name="check" size={16} color="#FF4747" />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

const dropdownStyles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#000000',
    minHeight: 36,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '2px 2px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  buttonText: {
    flex: 1,
    fontSize: 13,
    fontFamily: 'Archivo',
    color: '#000000',
    marginRight: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    maxHeight: 300,
    minWidth: 200,
    maxWidth: '80%',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  menu: {
    maxHeight: 280,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  optionFirst: {
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
  },
  optionLast: {
    borderBottomWidth: 0,
    borderBottomLeftRadius: 9,
    borderBottomRightRadius: 9,
  },
  optionSelected: {
    backgroundColor: 'rgba(255, 71, 71, 0.1)',
  },
  optionText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: '#000000',
  },
  optionTextSelected: {
    fontWeight: '600',
    color: '#FF4747',
  },
});

const TigerTeamScreen = () => {
  const { palette } = useTheme();
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const { 
    availableWidgets, 
    widgetStatuses, 
    openPersistentWidget, 
    openReviewMode,
    completePersistentWidget
  } = usePersistentWidget();
  
  const [tigerTeamWidget, setTigerTeamWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [allStaff, setAllStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);

  useEffect(() => {
    // Only load data on initial mount or when user changes, not when editing
    if (!isEditing && !hasLoadedData) {
      loadTigerTeamData();
    }
  }, [user?.uid, availableWidgets, isEditing, hasLoadedData]);

  const loadTigerTeamData = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      // Find the Tiger Team widget
      const tigerWidget = availableWidgets.find(w => w.type === 'tiger_team_breakouts');
      setTigerTeamWidget(tigerWidget);

      if (tigerWidget) {
        // Load the latest completion data
        const periodKey = persistentWidgetService.getPeriodKey(
          tigerWidget.schedule?.type || 'weekly',
          new Date(),
          tigerWidget.schedule
        );
        const { completion } = await persistentWidgetService.getLatestCompletionInPeriod(
          tigerWidget.id,
          user.uid,
          periodKey
        );
        
        if (completion?.data) {
          setTeamData(completion.data);
          setAllStaff(completion.data.allStaff || []);
          // Load departments from saved data, or use defaults if none saved
          const savedDepartments = completion.data.departments;
          if (savedDepartments && savedDepartments.length > 0) {
            setDepartments(savedDepartments);
          } else {
            // Extract unique departments from existing staff data
            const existingDepts = [...new Set((completion.data.allStaff || []).map(s => s.department).filter(Boolean))];
            setDepartments(existingDepts.length > 0 ? existingDepts : ['Eligibility', 'Finance', 'IT']);
          }
        } else {
          // No saved data, use defaults
          setDepartments(['Eligibility', 'Finance', 'IT']);
        }
        setHasLoadedData(true);
      } else {
        // No widget found, use defaults
        setDepartments(['Eligibility', 'Finance', 'IT']);
        setHasLoadedData(true);
      }
    } catch (error) {
      console.error('Error loading tiger team data:', error);
      setDepartments(['Eligibility', 'Finance', 'IT']);
      setHasLoadedData(true);
    }
    
    setLoading(false);
  };

  const handleOpenWidget = () => {
    if (tigerTeamWidget) {
      const status = widgetStatuses[tigerTeamWidget.id];
      if (status?.completed) {
        openReviewMode(tigerTeamWidget.id);
      } else {
        openPersistentWidget(tigerTeamWidget.id);
      }
    }
  };

  // Calculate team stats
  const getTeamStats = () => {
    const activeStaff = allStaff.filter(s => s.active === 'Y');
    const uniqueDepartments = [...new Set(allStaff.map(s => s.department).filter(Boolean))];
    
    return {
      totalStaff: allStaff.length,
      activeStaff: activeStaff.length,
      departments: uniqueDepartments.length,
    };
  };

  // Get staff by department
  const getStaffByDepartment = () => {
    const grouped = {};
    
    allStaff.forEach(staff => {
      const dept = staff.department || 'Unassigned';
      if (!grouped[dept]) {
        grouped[dept] = [];
      }
      grouped[dept].push(staff);
    });
    
    return grouped;
  };

  // Get all staff names for "Reports To" dropdown
  const getStaffNames = () => {
    return allStaff.map(s => s.person).filter(Boolean);
  };

  // Add new staff member
  const addStaff = () => {
    setAllStaff([...allStaff, {
      person: '',
      title: '',
      department: departments[0] || 'Unassigned',
      reportsTo: '',
      type: 'FTE',
      active: 'Y',
      flsaStatus: 'Exempt',
    }]);
    setIsEditing(true);
  };

  // Remove staff member
  const removeStaff = (index) => {
    if (allStaff.length > 0) {
      const newStaff = allStaff.filter((_, i) => i !== index);
      setAllStaff(newStaff);
      if (!isEditing) setIsEditing(true);
    }
  };

  // Update staff field
  const updateStaff = (index, field, value) => {
    const newStaff = [...allStaff];
    newStaff[index] = { ...newStaff[index], [field]: value };
    setAllStaff(newStaff);
    if (!isEditing) setIsEditing(true);
  };

  // Move staff up
  const moveStaffUp = (index) => {
    if (index > 0) {
      const newStaff = [...allStaff];
      [newStaff[index - 1], newStaff[index]] = [newStaff[index], newStaff[index - 1]];
      setAllStaff(newStaff);
      if (!isEditing) setIsEditing(true);
    }
  };

  // Move staff down
  const moveStaffDown = (index) => {
    if (index < allStaff.length - 1) {
      const newStaff = [...allStaff];
      [newStaff[index], newStaff[index + 1]] = [newStaff[index + 1], newStaff[index]];
      setAllStaff(newStaff);
      if (!isEditing) setIsEditing(true);
    }
  };

  // Add new department
  const addDepartment = () => {
    if (newDepartment.trim() && !departments.includes(newDepartment.trim())) {
      setDepartments([...departments, newDepartment.trim()]);
      setNewDepartment('');
      if (!isEditing) setIsEditing(true);
    }
  };

  // Remove department
  const removeDepartment = (dept) => {
    // Check if staff are in this department
    const staffInDept = allStaff.filter(s => s.department === dept);
    if (staffInDept.length > 0) {
      // Offer to reassign staff to Unassigned
      Alert.alert(
        'Department Has Staff',
        `There are ${staffInDept.length} staff member(s) in ${dept}. Would you like to reassign them to "Unassigned" and remove this department?`,
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Reassign & Remove', 
            style: 'destructive',
            onPress: () => {
              // Reassign all staff in this department to Unassigned
              const updatedStaff = allStaff.map(s => 
                s.department === dept ? { ...s, department: 'Unassigned' } : s
              );
              setAllStaff(updatedStaff);
              // Make sure Unassigned is in departments if not already
              if (!departments.includes('Unassigned')) {
                setDepartments([...departments.filter(d => d !== dept), 'Unassigned']);
              } else {
                setDepartments(departments.filter(d => d !== dept));
              }
              if (!isEditing) setIsEditing(true);
            }
          }
        ]
      );
      return;
    }
    setDepartments(departments.filter(d => d !== dept));
    if (!isEditing) setIsEditing(true);
  };

  // Save changes
  const saveChanges = async () => {
    if (!user?.uid) {
      Alert.alert('Error', 'You must be logged in to save.');
      return;
    }

    // If no widget exists, we can still save to a generic location
    if (!tigerTeamWidget) {
      console.warn('No Tiger Team widget found, attempting to save anyway...');
    }

    setSaving(true);
    try {
      const periodKey = tigerTeamWidget 
        ? persistentWidgetService.getPeriodKey(
            tigerTeamWidget.schedule?.type || 'weekly',
            new Date(),
            tigerTeamWidget.schedule
          )
        : `manual-${new Date().toISOString().split('T')[0]}`;
      
      // Build department-specific data dynamically
      const departmentData = {};
      departments.forEach(dept => {
        const key = dept.toLowerCase().replace(/\s+/g, '_');
        departmentData[`${key}Data`] = allStaff.filter(s => s.department === dept && s.active === 'Y');
      });

      const completionData = {
        widgetId: tigerTeamWidget?.id || 'tiger_team_manual',
        userId: user.uid,
        periodKey,
        widgetType: 'tiger_team_breakouts',
        data: {
          allStaff,
          departments,
          ...departmentData,
        },
      };

      const result = await persistentWidgetService.saveCompletion(completionData);
      if (result.success) {
        Alert.alert('Success', 'Team data saved successfully!');
        setIsEditing(false);
        // Update teamData locally instead of reloading to prevent data loss
        setTeamData(completionData.data);
      } else {
        Alert.alert('Error', result.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving:', error);
      Alert.alert('Error', `Failed to save team data: ${error.message}`);
    }
    setSaving(false);
  };

  const stats = getTeamStats();
  const staffByDept = getStaffByDepartment();
  const staffNames = getStaffNames();

  // Build weekly actions for sidebar
  const weeklyActions = availableWidgets.map(widget => ({
    id: widget.id,
    title: widget.title,
    type: widget.type,
    schedule: widget.schedule,
    completed: widgetStatuses[widget.id]?.completed || false,
    onPress: () => {
      if (widgetStatuses[widget.id]?.completed) {
        openReviewMode(widget.id);
      } else {
        openPersistentWidget(widget.id);
      }
    },
  }));

  return (
    <View style={styles.container}>
      {/* Geometric Decorative Shapes */}
      <View style={[styles.geometricShape, styles.trianglePink]} />
      <View style={[styles.geometricShape, styles.circleYellow]} />
      <View style={[styles.geometricShape, styles.lineBlack]} />
      <View style={[styles.geometricShape, styles.triangleLavender]} />
      
      <View style={styles.layoutRow}>
        {/* Sidebar */}
        <DashboardSidebar 
          currentPage="team"
          weeklyActions={weeklyActions}
        />

        {/* Main Content */}
        <ScrollView style={styles.mainCol} contentContainerStyle={styles.mainContent}>
          {/* Header Card */}
          <View style={styles.headerCard}>
            <View style={styles.headerContent}>
              <View style={styles.headerLeft}>
                <Text style={styles.headerTitle}>My Team</Text>
                <Text style={styles.headerSubtitle}>
                  Tiger Team Breakouts & Performance Tracking
                </Text>
              </View>
              <View style={styles.headerActions}>
                <TouchableOpacity 
                  style={styles.addStaffButton}
                  onPress={addStaff}
                >
                  <MaterialCommunityIcons name="account-plus" size={18} color="#000" />
                  <Text style={styles.addStaffButtonText}>Add Staff</Text>
                </TouchableOpacity>
                {isEditing && (
                  <TouchableOpacity 
                    style={[styles.saveButton, saving && { opacity: 0.6 }]}
                    onPress={saveChanges}
                    disabled={saving}
                  >
                    <MaterialCommunityIcons name="content-save" size={18} color="#fff" />
                    <Text style={styles.saveButtonText}>{saving ? 'Saving...' : 'Save'}</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>

          {loading ? (
            <ActivityIndicator size="large" color={palette.primary || '#FF0600'} style={{ marginTop: 40 }} />
          ) : (
            <>
              {/* Stats Overview */}
              <View style={styles.statsRow}>
                <AnimatedCard style={styles.statCard}>
                  <MaterialCommunityIcons name="account-group" size={32} color="#FF4747" />
                  <Text style={styles.statNumber}>{stats.totalStaff}</Text>
                  <Text style={styles.statLabel}>Total Staff</Text>
                </AnimatedCard>
                <AnimatedCard style={styles.statCard}>
                  <MaterialCommunityIcons name="account-check" size={32} color="#4ADE80" />
                  <Text style={styles.statNumber}>{stats.activeStaff}</Text>
                  <Text style={styles.statLabel}>Active Staff</Text>
                </AnimatedCard>
                <AnimatedCard style={styles.statCard}>
                  <MaterialCommunityIcons name="office-building" size={32} color="#60A5FA" />
                  <Text style={styles.statNumber}>{stats.departments}</Text>
                  <Text style={styles.statLabel}>Departments</Text>
                </AnimatedCard>
              </View>

              {/* Departments Manager */}
              <AnimatedCard style={styles.departmentManagerCard}>
                <Text style={styles.sectionTitle}>Departments</Text>
                <View style={styles.departmentsList}>
                  {departments.map((dept, idx) => (
                    <View key={idx} style={styles.departmentTag}>
                      <Text style={styles.departmentTagText}>{dept}</Text>
                      <TouchableOpacity onPress={() => removeDepartment(dept)} style={styles.removeDeptBtn}>
                        <MaterialCommunityIcons name="close" size={14} color="#FF4747" />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
                <View style={styles.addDepartmentRow}>
                  <TextInput
                    style={styles.newDepartmentInput}
                    placeholder="New department name..."
                    placeholderTextColor="rgba(0,0,0,0.4)"
                    value={newDepartment}
                    onChangeText={setNewDepartment}
                    onSubmitEditing={addDepartment}
                  />
                  <TouchableOpacity style={styles.addDeptButton} onPress={addDepartment}>
                    <MaterialCommunityIcons name="plus" size={18} color="#fff" />
                  </TouchableOpacity>
                </View>
              </AnimatedCard>

              {/* Staff List by Department */}
              {Object.entries(staffByDept).map(([department, staff]) => (
                <AnimatedCard key={department} style={styles.departmentCard}>
                  <View style={styles.departmentHeader}>
                    <MaterialCommunityIcons 
                      name={department === 'IT' ? 'laptop' : department === 'Finance' ? 'calculator' : 'clipboard-check'} 
                      size={24} 
                      color="#FF4747" 
                    />
                    <Text style={styles.departmentTitle}>{department}</Text>
                    <View style={styles.departmentCountBadge}>
                      <Text style={styles.departmentCountText}>{staff.length}</Text>
                    </View>
                  </View>
                  
                  <View style={styles.staffList}>
                    {staff.map((member, idx) => {
                      const actualIndex = allStaff.findIndex(s => s === member);
                      return (
                        <View key={actualIndex} style={styles.staffItem}>
                          <View style={styles.staffAvatar}>
                            <Text style={styles.staffAvatarText}>
                              {member.person?.charAt(0)?.toUpperCase() || '?'}
                            </Text>
                          </View>
                          <View style={styles.staffInfo}>
                            <TextInput
                              style={styles.staffNameInput}
                              value={member.person}
                              onChangeText={(value) => updateStaff(actualIndex, 'person', value)}
                              placeholder="Name"
                              placeholderTextColor="rgba(0,0,0,0.4)"
                            />
                            <TextInput
                              style={styles.staffTitleInput}
                              value={member.title}
                              onChangeText={(value) => updateStaff(actualIndex, 'title', value)}
                              placeholder="Title"
                              placeholderTextColor="rgba(0,0,0,0.4)"
                            />
                            {/* Department Dropdown */}
                            <View style={styles.reportsToRow}>
                              <Text style={styles.reportsToLabel}>Department:</Text>
                              <Dropdown
                                value={member.department || 'Unassigned'}
                                options={[...departments, 'Unassigned']}
                                onSelect={(dept) => updateStaff(actualIndex, 'department', dept)}
                                placeholder="Select Department"
                              />
                            </View>
                            {/* Reports To Dropdown */}
                            <View style={styles.reportsToRow}>
                              <Text style={styles.reportsToLabel}>Reports to:</Text>
                              <Dropdown
                                value={member.reportsTo || 'None'}
                                options={['None', ...staffNames.filter(n => n !== member.person)]}
                                onSelect={(name) => updateStaff(actualIndex, 'reportsTo', name === 'None' ? '' : name)}
                                placeholder="Select Manager"
                              />
                            </View>
                          </View>
                          <View style={styles.staffActions}>
                            <View style={styles.staffTypeDropdown}>
                              <Dropdown
                                value={member.type || 'FTE'}
                                options={['FTE', 'Part Time', 'Consultant']}
                                onSelect={(type) => updateStaff(actualIndex, 'type', type)}
                                placeholder="Type"
                              />
                            </View>
                            <View style={styles.staffControlButtons}>
                              <TouchableOpacity onPress={() => moveStaffUp(actualIndex)} style={styles.moveBtn}>
                                <MaterialCommunityIcons name="chevron-up" size={18} color="#000" />
                              </TouchableOpacity>
                              <TouchableOpacity onPress={() => moveStaffDown(actualIndex)} style={styles.moveBtn}>
                                <MaterialCommunityIcons name="chevron-down" size={18} color="#000" />
                              </TouchableOpacity>
                              <TouchableOpacity onPress={() => removeStaff(actualIndex)} style={styles.removeBtn}>
                                <MaterialCommunityIcons name="trash-can-outline" size={18} color="#FF4747" />
                              </TouchableOpacity>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                    
                    {staff.length === 0 && (
                      <Text style={styles.noStaffText}>No staff in this department</Text>
                    )}
                  </View>
                </AnimatedCard>
              ))}

              {allStaff.length === 0 && (
                <AnimatedCard style={styles.emptyCard}>
                  <MaterialCommunityIcons name="account-plus" size={48} color="rgba(0,0,0,0.3)" />
                  <Text style={styles.emptyTitle}>No Team Members Yet</Text>
                  <Text style={styles.emptyText}>
                    Click "Add Staff" to add your team members and start tracking performance.
                  </Text>
                </AnimatedCard>
              )}
            </>
          )}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    position: 'relative',
    paddingTop: 160,
    ...Platform.select({
      web: {
        minHeight: 'calc(100vh - 80px)',
        overflow: 'visible',
      },
    }),
  },
  layoutRow: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    maxWidth: 1600,
    marginHorizontal: 'auto',
    paddingHorizontal: 32,
    paddingVertical: 32,
    gap: 24,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  mainCol: {
    flex: 1,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  mainContent: {
    paddingBottom: 48,
    gap: 24,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  // Header Card
  headerCard: {
    backgroundColor: '#FF4747',
    borderRadius: 20,
    padding: 24,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '6px 8px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  headerLeft: {
    flex: 1,
    minWidth: 200,
  },
  headerTitle: {
    fontSize: 32,
    fontFamily: 'Chivo',
    fontWeight: '700',
    color: '#F1F1F1',
    marginBottom: 4,
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  headerSubtitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    color: 'rgba(255,255,255,0.8)',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.3)',
      },
    }),
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  addStaffButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'white',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.8)',
        cursor: 'pointer',
      },
    }),
  },
  addStaffButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000000',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#4ADE80',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.8)',
        cursor: 'pointer',
      },
    }),
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#FFFFFF',
  },
  // Stats Row
  statsRow: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  statCard: {
    flex: 1,
    minWidth: 150,
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  statNumber: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '700',
    color: '#000000',
    marginTop: 8,
  },
  statLabel: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '500',
    color: 'rgba(0,0,0,0.6)',
    marginTop: 4,
  },
  // Department Manager
  departmentManagerCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '700',
    color: '#000000',
    marginBottom: 12,
  },
  departmentsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  departmentTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E0F2FE',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: '#000000',
    gap: 6,
  },
  departmentTagText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#000000',
  },
  removeDeptBtn: {
    padding: 2,
  },
  addDepartmentRow: {
    flexDirection: 'row',
    gap: 8,
  },
  newDepartmentInput: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    fontFamily: 'Archivo',
    borderWidth: 2,
    borderColor: '#000000',
    color: '#000000',
  },
  addDeptButton: {
    backgroundColor: '#4ADE80',
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.8)',
        cursor: 'pointer',
      },
    }),
  },
  // Department Cards
  departmentCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  departmentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  departmentTitle: {
    flex: 1,
    fontSize: 20,
    fontFamily: 'Chivo',
    fontWeight: '700',
    color: '#000000',
  },
  departmentCountBadge: {
    backgroundColor: '#FF4747',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderWidth: 2,
    borderColor: '#000000',
  },
  departmentCountText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'white',
  },
  // Staff List
  staffList: {
    gap: 16,
  },
  staffItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  staffAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FF4747',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#000000',
  },
  staffAvatarText: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'white',
  },
  staffInfo: {
    flex: 1,
    gap: 6,
  },
  staffNameInput: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#000000',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    paddingBottom: 4,
    paddingHorizontal: 0,
  },
  staffTitleInput: {
    fontSize: 13,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 0,
  },
  reportsToRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  reportsToLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.5)',
  },
  reportsToDropdown: {
    flex: 1,
  },
  reportsToSelect: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  reportsToSelectText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    color: '#000000',
  },
  staffActions: {
    alignItems: 'flex-end',
    gap: 8,
    minWidth: 110,
  },
  staffTypeDropdown: {
    width: '100%',
  },
  staffControlButtons: {
    flexDirection: 'row',
    gap: 4,
  },
  moveBtn: {
    padding: 4,
    backgroundColor: 'white',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#000000',
  },
  removeBtn: {
    padding: 4,
    backgroundColor: 'white',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FF4747',
  },
  noStaffText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.4)',
    textAlign: 'center',
    paddingVertical: 16,
  },
  // Empty State
  emptyCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 40,
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  emptyTitle: {
    fontSize: 20,
    fontFamily: 'Chivo',
    fontWeight: '700',
    color: '#000000',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    textAlign: 'center',
    maxWidth: 300,
  },
  // Geometric Shapes
  geometricShape: {
    position: 'absolute',
    zIndex: -1,
  },
  trianglePink: {
    width: 0,
    height: 0,
    borderLeftWidth: 80,
    borderRightWidth: 80,
    borderBottomWidth: 140,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'rgba(255,182,193,0.3)',
    top: 100,
    right: 50,
    transform: [{ rotate: '15deg' }],
  },
  circleYellow: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(255,223,128,0.4)',
    bottom: 150,
    left: 30,
  },
  lineBlack: {
    width: 100,
    height: 4,
    backgroundColor: 'rgba(0,0,0,0.15)',
    top: 300,
    left: 100,
    transform: [{ rotate: '-30deg' }],
  },
  triangleLavender: {
    width: 0,
    height: 0,
    borderLeftWidth: 50,
    borderRightWidth: 50,
    borderBottomWidth: 90,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'rgba(200,180,220,0.3)',
    bottom: 80,
    right: 120,
    transform: [{ rotate: '-10deg' }],
  },
});

export default TigerTeamScreen;
